import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import { stoneBiome } from '../biomeArchetypes';
import { Z_LAYERS } from '../../../game/rendering/Z_LAYERS';

/** Fossil-graveyard — a dry stone vault where ancient whale skeletons lie
 *  half-buried in the sediment, their ribcages forming bone arches. The
 *  leviathans kind with speed 0 (bob only) becomes static architecture —
 *  the whales don't drift, they just breathe gently with the bob. Proves
 *  the leviathans kind works as set-dressing, not just moving creatures. */
export const FOSSIL_GRAVEYARD = stoneBiome({
  id: 'fossil_graveyard',
  sky: { from: 0x1a1812, to: 0x0e0c08 },
  motes: { colors: [0x6a5a3a, 0x4a3a2a], count: 10, sizeMin: 2, sizeMax: 5, driftPx: 12, periodMinMs: 12000, periodMaxMs: 24000, alphaMin: 0.15, alphaMax: 0.35 },
  glow: {
    blobs: [
      { color: 0x4a3a2a, atX: 0.3, atY: 0.7, size: 300, alpha: 0.04 },
      { color: 0x6a5a3a, atX: 0.7, atY: 0.6, size: 250, alpha: 0.03 },
    ],
    breathe: { periodMs: 10000, min: 0.7, max: 1.3 },
  },
  lightField: { palette: [0x4a3a2a, 0x6a5a3a], count: 6, sizeMin: 80, sizeMax: 180, alphaMin: 0.02, alphaMax: 0.06, driftRadius: 30, periodMinMs: 14000, periodMaxMs: 28000 },
  flicker: { color: 0x6a5a3a, size: 150, baseAlpha: 0.03, flickerAmp: 0.02, hzMin: 0.3, hzMax: 1.2, anchors: [{ atX: 0.3, atY: 0.7 }, { atX: 0.7, atY: 0.6 }] },
  extraLayers: [
    // The fossil whales: whaleSkeleton silhouettes with speed 0 — they don't
    // drift, just bob gently. Half-buried bone arches, not swimming creatures.
    // Multiple planes at different depths = a graveyard of varied sizes.
    {
      kind: 'leviathans',
      shape: 'whaleSkeleton',
      planes: [
        { count: 2, widthMin: 500, widthMax: 800, speedMin: 0, speedMax: 0, alpha: 0.18, tint: 0x3a2a1a, depth: Z_LAYERS.LEVIATHAN_FAR },
        { count: 1, widthMin: 800, widthMax: 1200, speedMin: 0, speedMax: 0, alpha: 0.25, tint: 0x4a3a2a, depth: Z_LAYERS.LEVIATHAN_NEAR },
      ],
      bob: { ampPx: 8, periodMinMs: 15000, periodMaxMs: 30000 },
      edgeHighlight: true,
      edgeColor: 0x8a7a5a,
      detail: 1,
      direction: 'both',
      bandYMin: 0.3,
      bandYMax: 0.7,
    },
  ],
  parallax: [
    { svg: OTHERWORLD_BACKDROPS.fossil.far, shape: 'columns', color: 0x2a2418, speed: 0.01, height: 500, alpha: 0.55, detail: 0.5 },
    { svg: OTHERWORLD_BACKDROPS.fossil.mid, shape: 'columns', color: 0x1e180e, speed: 0.03, height: 350, alpha: 0.65, detail: 0.7 },
    { svg: OTHERWORLD_BACKDROPS.fossil.near, shape: 'columns', color: 0x120e08, speed: 0.08, height: 220, alpha: 0.75, detail: 1.0 },
  ],
  fog: { color: 0x1a1812, bands: 3, alphaMin: 0.05, alphaMax: 0.12, driftPx: 20, periodMinMs: 14000, periodMaxMs: 28000 },
  closer: { vignette: { color: 0x000000, edgeAlpha: 0.35 }, grade: { top: 0x1a1812, bottom: 0x080604, strength: 0.10, blend: 'normal' } },
});
